/* ==========================================================================
   Harper Reborn — motion + small behaviours for harper-* sections
   - scroll reveal            [data-hp-reveal]
   - counters                 [data-hp-count][data-to][data-dec] (locale-aware, keeps prefix/suffix)
   - rings                    [data-hp-ring][data-pct] (svg) / [data-hp-conic][data-pct]
   - tabs                     [data-hp-tabs] > [role=tab][aria-controls] (+ [data-hp-tabs-slider] swipe mode)
   - click-to-play video      [data-hp-video]
   - play/pause toggle        [data-hp-vwrap] > video + [data-hp-vtoggle]
   - marquee fill             [data-hp-marquee] (clones items until 2× viewport)
   - sticky add-to-cart bar   [data-hp-sticky-atc] (listens to hp:qb:change from the
                              quantity-breaks block to show the tier total)
   - scroll-to-top            [data-hp-totop]
   - before/after slider      [data-hp-compare] (--hp-ba-pos, pointer drag + range input)
   - promo popup              [data-hp-popup] (delay, once per N days, focus trap, editor-aware)
   - ambient video            [data-hp-ambient] video (plays only while on screen)
   - click-to-copy            [data-hp-copy] (swaps the label with data-hp-copied)

   Reveal failsafe: the CSS only hides [data-hp-reveal] elements while BOTH
   html.hp-js and html.hp-motion-ready are present. This file adds hp-js at
   the top and hp-motion-ready immediately before the synchronous initReveal
   pass, so on-screen elements are marked visible in the same task and a
   script that never runs (or throws earlier) never hides anything.
   Everything respects prefers-reduced-motion and re-initialises on
   shopify:section:load; per-section listeners are torn down on
   shopify:section:unload.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  root.classList.add('hp-js');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsIO = 'IntersectionObserver' in window;
  var lang = root.getAttribute('lang') || 'en';

  /* ---------------- per-section cleanup registry ---------------- */
  var cleanups = typeof WeakMap === 'function' ? new WeakMap() : null;
  function onUnload(el, fn) {
    if (!cleanups || !el || !el.closest) return;
    var sec = el.closest('.shopify-section');
    if (!sec) return;
    var list = cleanups.get(sec) || [];
    list.push(fn);
    cleanups.set(sec, list);
  }

  /* ---------------- reveal ---------------- */
  var revealIO = supportsIO
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            enter(e.target);
            revealIO.unobserve(e.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.02 }
      )
    : null;

  function enter(el) {
    el.classList.add('is-in');
    runCounters(el);
    runRings(el);
  }

  function initReveal(scope) {
    var els = [].slice.call(scope.querySelectorAll('[data-hp-reveal]'));
    if (scope.matches && scope.matches('[data-hp-reveal]')) els.unshift(scope);
    var vh = window.innerHeight || 800;
    // Read every rect first, then write, so the pass never interleaves layout reads and writes.
    var rects = els.map(function (el) {
      return el.classList.contains('is-in') ? null : el.getBoundingClientRect();
    });
    els.forEach(function (el, i) {
      var r = rects[i];
      if (!r) return;
      // Anything already on screen at init is shown immediately (no pop-in).
      if (reduce || !revealIO || r.top < vh * 0.92) {
        enter(el);
      } else {
        revealIO.observe(el);
      }
    });
  }

  /* Counters / rings that are NOT inside a reveal container still animate when they scroll in. */
  var statIO = supportsIO
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            runCounters(e.target);
            runRings(e.target);
            statIO.unobserve(e.target);
          });
        },
        { threshold: 0.1 }
      )
    : null;

  function initStats(scope) {
    scope.querySelectorAll('[data-hp-count], [data-hp-ring], [data-hp-conic]').forEach(function (n) {
      if (n.dataset.hpDone || n.closest('[data-hp-reveal]')) return;
      var host = n.closest('[data-hp-conic]') || n;
      if (reduce || !statIO) {
        runCounters(host);
        runRings(host);
      } else {
        statIO.observe(host);
      }
    });
  }

  /* ---------------- counters ---------------- */
  function easeOut(p) {
    return 1 - Math.pow(1 - p, 3);
  }

  /* "1.250" / "1,250" → 1250 · "4.8" / "4,8" → 4.8 · "50K+" → 50 with suffix "K+" · "%96" → 96 with prefix "%".
     A single separator followed by exactly three digits is read as a thousands group. */
  function parseCount(raw) {
    var s = String(raw == null ? '' : raw).trim();
    var m = s.match(/^([^\d]*?)([\d.,\s ']+)([^\d]*)$/);
    if (!m) return null;
    var num = m[2].replace(/[\s ']/g, '');
    var dec = 0;
    var lastDot = num.lastIndexOf('.');
    var lastComma = num.lastIndexOf(',');
    var lastSep = Math.max(lastDot, lastComma);
    var intPart = num;
    var fracPart = '';
    if (lastSep > -1) {
      var sepChar = num.charAt(lastSep);
      var after = num.slice(lastSep + 1);
      var before = num.slice(0, lastSep);
      var otherSep = sepChar === '.' ? ',' : '.';
      var mixed = before.indexOf(otherSep) > -1; // "1,234.5" → the last one is the decimal
      var repeated = before.indexOf(sepChar) > -1; // "1.234.567" → thousands
      var isDecimal = mixed || (!repeated && after.length !== 3);
      if (isDecimal) {
        intPart = before;
        fracPart = after;
        dec = after.length;
      }
    }
    var value = parseFloat(intPart.replace(/[.,]/g, '') + (fracPart ? '.' + fracPart : ''));
    if (isNaN(value)) return null;
    return { prefix: m[1], suffix: m[3], value: value, dec: dec };
  }

  function formatNumber(n, dec) {
    try {
      return new Intl.NumberFormat(lang, { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n);
    } catch (e) {
      return n.toFixed(dec);
    }
  }

  function runCounters(scope) {
    var nodes = scope.matches && scope.matches('[data-hp-count]') ? [scope] : [];
    nodes = nodes.concat([].slice.call(scope.querySelectorAll('[data-hp-count]')));
    nodes.forEach(function (n) {
      if (n.dataset.hpDone) return;
      n.dataset.hpDone = '1';
      var parsed = parseCount(n.dataset.to != null ? n.dataset.to : n.textContent);
      if (!parsed) return; // not numeric → leave the server-rendered text alone
      var dec = n.dataset.dec != null && n.dataset.dec !== '' ? parseInt(n.dataset.dec, 10) : parsed.dec;
      if (isNaN(dec)) dec = parsed.dec;
      var to = parsed.value;
      function paint(v) {
        n.textContent = parsed.prefix + formatNumber(v, dec) + parsed.suffix;
      }
      if (reduce) {
        paint(to);
        return;
      }
      var t0 = null;
      var dur = 1400;
      function step(t) {
        if (!t0) t0 = t;
        var p = Math.min(1, (t - t0) / dur);
        paint(to * easeOut(p));
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }

  /* ---------------- rings ---------------- */
  function runRings(scope) {
    var rings = scope.matches && scope.matches('[data-hp-ring]') ? [scope] : [];
    rings = rings.concat([].slice.call(scope.querySelectorAll('[data-hp-ring]')));
    rings.forEach(function (c) {
      if (c.dataset.hpDone) return;
      c.dataset.hpDone = '1';
      var pct = Math.max(0, Math.min(100, parseFloat(c.dataset.pct || '0')));
      var r = parseFloat(c.getAttribute('r') || '26');
      var len = 2 * Math.PI * r;
      c.style.strokeDasharray = len;
      c.style.strokeDashoffset = len;
      var target = len * (1 - pct / 100);
      if (reduce) {
        c.style.transition = 'none';
        c.style.strokeDashoffset = target;
        return;
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          c.style.strokeDashoffset = target;
        });
      });
    });

    var conics = scope.matches && scope.matches('[data-hp-conic]') ? [scope] : [];
    conics = conics.concat([].slice.call(scope.querySelectorAll('[data-hp-conic]')));
    conics.forEach(function (r) {
      if (r.dataset.hpDone) return;
      r.dataset.hpDone = '1';
      var p = Math.max(0, Math.min(100, parseFloat(r.dataset.pct || '0')));
      if (reduce) {
        r.style.setProperty('--p', p + '%');
        return;
      }
      var c = 0;
      var iv = setInterval(function () {
        c += 2;
        r.style.setProperty('--p', Math.min(c, p) + '%');
        if (c >= p) clearInterval(iv);
      }, 18);
      onUnload(r, function () {
        clearInterval(iv);
      });
    });
  }

  /* ---------------- video helpers ---------------- */
  function playVideo(v) {
    if (v.dataset.src && !v.getAttribute('src')) v.setAttribute('src', v.dataset.src);
    var pr = v.play();
    if (pr && pr.catch) pr.catch(function () {});
  }

  /* [data-hp-vwrap] holds a <video> and a [data-hp-vtoggle] button. The button
     reflects the real play state and remembers a deliberate pause so the
     autoplay helpers (tabs, ambient) do not restart the video. */
  function initVideoToggles(scope) {
    scope.querySelectorAll('[data-hp-vtoggle]').forEach(function (btn) {
      if (btn.dataset.hpBound) return;
      btn.dataset.hpBound = '1';
      var wrap = btn.closest('[data-hp-vwrap]');
      var video = wrap ? wrap.querySelector('video') : null;
      if (!video) {
        btn.hidden = true;
        return;
      }
      function sync() {
        var playing = !video.paused && !video.ended;
        btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
        btn.setAttribute('aria-label', playing ? btn.dataset.labelPause || 'Pause' : btn.dataset.labelPlay || 'Play');
      }
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (video.paused) {
          delete video.dataset.hpUserPaused;
          playVideo(video);
        } else {
          video.dataset.hpUserPaused = '1';
          video.pause();
        }
      });
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      video.addEventListener('ended', sync);
      sync();
    });
  }

  /* ---------------- tabs (+ optional swipe-strip mode) ----------------
     [data-hp-tabs] wraps the [role=tab] buttons AND their panels.
     data-hp-tabs-slider="(max-width: 749px)" turns on strip mode while that
     media query matches: panels inside [data-hp-tabs-panels] are all shown
     side by side (CSS makes the wrapper a snap-scrolling row), tapping a tab
     scrolls to its panel, and swiping highlights the matching tab. Outside
     the query it is a normal tab set: one panel shown, the rest hidden. */
  function initTabs(scope) {
    scope.querySelectorAll('[data-hp-tabs]').forEach(function (group) {
      if (group.dataset.hpBound) return;
      group.dataset.hpBound = '1';
      var tabs = [].slice.call(group.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      var panels = tabs.map(function (t) {
        return doc.getElementById(t.getAttribute('aria-controls'));
      });
      var strip = group.querySelector('[data-hp-tabs-panels]');
      var mq = group.dataset.hpTabsSlider && window.matchMedia ? window.matchMedia(group.dataset.hpTabsSlider) : null;
      var slider = false;
      var io = null;
      var viewIO = null;
      var current = null;
      var lockUntil = 0; // ignore scroll-driven selection while we scroll programmatically
      // Videos only get a src (= start downloading) once the group is on screen; the
      // theme editor re-renders sections often and sources add up fast otherwise.
      var inView = !supportsIO;

      function playOnly(panel) {
        panels.forEach(function (p) {
          if (!p) return;
          p.querySelectorAll('video').forEach(function (v) {
            if (p === panel && inView && !reduce && !v.dataset.hpUserPaused) {
              playVideo(v);
            } else {
              v.pause();
            }
          });
        });
      }

      if (supportsIO) {
        viewIO = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              inView = e.isIntersecting;
              playOnly(inView && current ? panels[tabs.indexOf(current)] : null);
            });
          },
          { threshold: 0.15 }
        );
        viewIO.observe(group);
      }

      function centerIn(list, el) {
        if (!list || !el || list.scrollWidth <= list.clientWidth) return;
        var lr = list.getBoundingClientRect();
        var er = el.getBoundingClientRect();
        var left = list.scrollLeft + (er.left - lr.left) - (list.clientWidth - er.width) / 2;
        list.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' });
      }

      function select(tab) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        current = tab;
        centerIn(tab.parentElement, tab); // keep the active pill visible in a scrolling tablist
      }

      function activate(tab, scroll) {
        select(tab);
        var panel = panels[tabs.indexOf(tab)];
        if (slider) {
          if (panel && strip && scroll !== false) {
            lockUntil = Date.now() + 800;
            var sr = strip.getBoundingClientRect();
            var pr = panel.getBoundingClientRect();
            var pad = parseFloat(getComputedStyle(strip).paddingLeft) || 0;
            strip.scrollTo({ left: strip.scrollLeft + (pr.left - sr.left) - pad, behavior: reduce ? 'auto' : 'smooth' });
          }
        } else {
          panels.forEach(function (p, i) {
            if (p) p.hidden = tabs[i] !== tab;
          });
        }
        playOnly(panel);
      }

      function setMode() {
        slider = !!(mq && mq.matches && strip);
        if (io) {
          io.disconnect();
          io = null;
        }
        if (slider) {
          panels.forEach(function (p) {
            if (p) p.hidden = false;
          });
          if (supportsIO) {
            io = new IntersectionObserver(
              function (entries) {
                if (Date.now() < lockUntil) return;
                entries.forEach(function (e) {
                  if (!e.isIntersecting) return;
                  var i = panels.indexOf(e.target);
                  if (i > -1 && tabs[i] !== current) {
                    select(tabs[i]);
                    playOnly(e.target);
                  }
                });
              },
              { root: strip, threshold: 0.6 }
            );
            panels.forEach(function (p) {
              if (p) io.observe(p);
            });
          }
        }
        activate(current || tabs[0], false);
      }

      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          activate(t);
        });
        t.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (e.key === 'Home') dir = -i;
          if (e.key === 'End') dir = tabs.length - 1 - i;
          if (!dir) return;
          e.preventDefault();
          var next = tabs[(i + dir + tabs.length) % tabs.length];
          next.focus();
          activate(next);
        });
      });

      current = tabs.filter(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      })[0] || tabs[0];
      setMode();
      if (mq) {
        if (mq.addEventListener) mq.addEventListener('change', setMode);
        else if (mq.addListener) mq.addListener(setMode);
      }
      group.hpActivate = activate; // used by the theme-editor block:select hook
      onUnload(group, function () {
        if (io) io.disconnect();
        if (viewIO) viewIO.disconnect();
        if (mq) {
          if (mq.removeEventListener) mq.removeEventListener('change', setMode);
          else if (mq.removeListener) mq.removeListener(setMode);
        }
      });
    });
  }

  /* ---------------- click-to-play video ---------------- */
  function initVideos(scope) {
    scope.querySelectorAll('[data-hp-video]').forEach(function (wrap) {
      if (wrap.dataset.hpBound) return;
      wrap.dataset.hpBound = '1';
      var video = wrap.querySelector('video');
      if (!video) return;
      function toggle() {
        if (video.paused) {
          // Only one proof video plays at a time.
          doc.querySelectorAll('[data-hp-video] video').forEach(function (v) {
            if (v !== video) {
              v.pause();
              v.closest('[data-hp-video]').classList.remove('is-playing');
            }
          });
          video.muted = false;
          playVideo(video);
        } else {
          video.pause();
        }
      }
      wrap.addEventListener('click', function (e) {
        e.preventDefault();
        toggle();
      });
      wrap.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle();
        }
      });
      video.addEventListener('play', function () {
        wrap.classList.add('is-playing');
      });
      video.addEventListener('pause', function () {
        wrap.classList.remove('is-playing');
      });
      video.addEventListener('ended', function () {
        wrap.classList.remove('is-playing');
      });
    });
  }

  /* ---------------- marquee fill ---------------- */
  function initMarquee(scope) {
    scope.querySelectorAll('[data-hp-marquee]').forEach(function (m) {
      if (m.dataset.hpBound) return;
      m.dataset.hpBound = '1';
      var track = m.querySelector('.hp-marquee-track');
      if (!track) return;
      var seqs = [].slice.call(track.children);
      if (seqs.length < 2) return;
      var base = seqs[0];
      function fill() {
        var vw = m.clientWidth || window.innerWidth;
        var items = [].slice.call(base.children).filter(function (c) {
          return !c.dataset.hpClone;
        });
        if (!items.length) return;
        // Drop earlier clones, then re-clone until each sequence covers the viewport.
        seqs.forEach(function (s) {
          [].slice.call(s.children).forEach(function (c) {
            if (c.dataset.hpClone) s.removeChild(c);
          });
        });
        // One measurement decides how many copies are needed (no re-measuring per batch).
        var baseWidth = base.scrollWidth || 1;
        var copies = Math.min(12, Math.max(0, Math.ceil((vw + 10) / baseWidth) - 1));
        for (var k = 0; k < copies; k++) {
          items.forEach(function (it) {
            seqs.forEach(function (s) {
              var cl = it.cloneNode(true);
              cl.dataset.hpClone = '1';
              cl.setAttribute('aria-hidden', 'true');
              cl.querySelectorAll('a, button, [tabindex]').forEach(function (f) {
                f.setAttribute('tabindex', '-1');
              });
              if (cl.hasAttribute('href')) cl.setAttribute('tabindex', '-1');
              s.appendChild(cl);
            });
          });
        }
      }
      fill();
      var t;
      function onResize() {
        clearTimeout(t);
        t = setTimeout(fill, 160);
      }
      function onVis() {
        m.classList.toggle('hp-marquee-paused', doc.hidden);
      }
      window.addEventListener('resize', onResize);
      doc.addEventListener('visibilitychange', onVis);
      onUnload(m, function () {
        clearTimeout(t);
        window.removeEventListener('resize', onResize);
        doc.removeEventListener('visibilitychange', onVis);
      });
    });
  }

  /* ---------------- sticky add-to-cart ---------------- */
  function initStickyAtc(scope) {
    scope.querySelectorAll('[data-hp-sticky-atc]').forEach(function (bar) {
      if (bar.dataset.hpBound) return;
      bar.dataset.hpBound = '1';
      var sectionId = bar.dataset.section;
      var anchor = doc.getElementById('ProductSubmitButton-' + sectionId);
      var btn = bar.querySelector('[data-hp-sticky-submit]');
      var priceOut = bar.querySelector('[data-hp-sticky-price]');
      var variantOut = bar.querySelector('[data-hp-sticky-variant]');
      if (!anchor || !btn) return;

      function syncPrice() {
        var priceEl = doc.getElementById('price-' + sectionId);
        if (!priceEl || !priceOut || bar.dataset.hpQb) return;
        var price = priceEl.querySelector('.price');
        var node = null;
        if (price && price.classList.contains('price--on-sale')) {
          node = price.querySelector('.price__sale .price-item--sale');
        }
        if (!node) node = priceEl.querySelector('.price__regular .price-item--regular');
        if (node) priceOut.textContent = node.textContent.trim();
      }

      var btnLabel = btn.querySelector('span');
      var ownLabel = btnLabel ? btnLabel.textContent.trim() : '';

      function syncVariant(variant) {
        if (!variantOut) return;
        if (variant && variant.title && variant.title !== 'Default Title') {
          variantOut.textContent = variant.title.replace(/\s*\/\s*/g, ' · ');
          variantOut.hidden = false;
        } else {
          variantOut.hidden = true;
        }
      }

      // Mirror the real button's state: disabled / sold out / unavailable. The
      // merchant's own label is kept while the product can be bought; only the
      // sold-out / unavailable wording is copied from the main button.
      function syncState() {
        var disabled = !!(anchor.hasAttribute('disabled') || anchor.getAttribute('aria-disabled') === 'true');
        btn.disabled = disabled;
        var label = anchor.querySelector('span');
        if (btnLabel) btnLabel.textContent = disabled && label ? label.textContent.trim() : ownLabel;
      }

      function setVisible(on) {
        bar.classList.toggle('is-visible', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (on) bar.removeAttribute('inert');
        else bar.setAttribute('inert', '');
      }

      btn.addEventListener('click', function () {
        anchor.click();
        // If the form rejects the add (quantity rule, required field…) the
        // error appears next to the real button: bring it into view.
        setTimeout(function () {
          var form = anchor.closest('product-form');
          var err = form && form.querySelector('.product-form__error-message-wrapper');
          if (err && !err.hidden) anchor.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        }, 600);
      });

      // Quantity-breaks block: while a tier is selected the bar shows the tier
      // total instead of the unit price (see assets/hp-quantity-breaks.js).
      doc.addEventListener('hp:qb:change', function (e) {
        if (!e.target || e.target.dataset.section !== sectionId || !priceOut) return;
        var d = e.detail || {};
        if (d.totalText) {
          bar.dataset.hpQb = '1';
          priceOut.textContent = d.totalText;
        } else {
          delete bar.dataset.hpQb;
          syncPrice();
        }
      });

      if (supportsIO) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              var below = e.boundingClientRect.top < 0; // scrolled past the real button
              setVisible(!e.isIntersecting && below);
            });
          },
          { threshold: 0 }
        );
        io.observe(anchor);
      }

      // The main button's disabled state and label change outside variantChange too
      // (non-existent option combination → setUnavailable(), cart updates, apps).
      if (typeof MutationObserver === 'function') {
        var mo = new MutationObserver(function () { syncState(); });
        mo.observe(anchor, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'], childList: true, subtree: true, characterData: true });
      }

      // PUB_SUB_EVENTS is a top-level `const` in constants.js (a global binding, not a window property).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          var d = event && event.data ? event.data : null;
          if (!d || (d.sectionId && d.sectionId !== sectionId)) return;
          var variant = d.variant;
          requestAnimationFrame(function () {
            syncPrice();
            syncVariant(variant);
            syncState();
          });
        });
      }
      setVisible(false);
      syncPrice();
      syncVariant(null);
      syncState();
    });
  }

  /* ---------------- scroll-to-top ---------------- */
  function initToTop() {
    var btn = doc.querySelector('[data-hp-totop]');
    if (!btn || btn.dataset.hpBound) return;
    btn.dataset.hpBound = '1';
    btn.hidden = false;
    var ticking = false;
    function check() {
      var on = window.scrollY > (window.innerHeight || 800) * 0.8;
      btn.classList.toggle('is-visible', on);
      btn.setAttribute('aria-hidden', on ? 'false' : 'true');
      btn.tabIndex = on ? 0 : -1;
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(check);
        }
      },
      { passive: true }
    );
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
    check();
  }

  /* ---------------- before / after slider ----------------
     [data-hp-compare] root exposes --hp-ba-pos (0–100%). Dragging anywhere on
     the root moves the divider; the <input type=range> inside keeps it
     keyboard-accessible. */
  function initCompare(scope) {
    scope.querySelectorAll('[data-hp-compare]').forEach(function (root) {
      if (root.dataset.hpBound) return;
      root.dataset.hpBound = '1';
      var range = root.querySelector('input[type="range"]');
      var dragging = false;

      function set(pct) {
        pct = Math.max(0, Math.min(100, pct));
        root.style.setProperty('--hp-ba-pos', pct + '%');
        if (range && Math.round(parseFloat(range.value)) !== Math.round(pct)) range.value = Math.round(pct);
      }
      function fromEvent(e) {
        var r = root.getBoundingClientRect();
        if (!r.width) return;
        set(((e.clientX - r.left) / r.width) * 100);
      }

      root.addEventListener('pointerdown', function (e) {
        if (e.target === range) return;
        dragging = true;
        root.classList.add('is-dragging');
        if (root.setPointerCapture) root.setPointerCapture(e.pointerId);
        fromEvent(e);
        e.preventDefault();
      });
      root.addEventListener('pointermove', function (e) {
        if (dragging) fromEvent(e);
      });
      function end() {
        dragging = false;
        root.classList.remove('is-dragging');
      }
      root.addEventListener('pointerup', end);
      root.addEventListener('pointercancel', end);
      root.addEventListener('lostpointercapture', end);
      if (range) {
        range.addEventListener('input', function () {
          set(parseFloat(range.value));
        });
      }
      set(parseFloat(root.dataset.hpCompare || (range && range.value) || '50'));
    });
  }

  /* ---------------- promo popup ----------------
     [data-hp-popup] data-hp-popup-delay="5" data-hp-popup-days="7"
     data-hp-popup-key="…" [data-hp-popup-open] (force open, e.g. after a form post).
     Inside: [data-hp-popup-close] buttons and a .hp-popup__bg backdrop.
     It waits while another dialog is open, traps Tab inside, closes on
     Escape and returns focus to where it was. */
  function isVisible(el) {
    if (!el || !el.getClientRects().length) return false;
    var cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && parseFloat(cs.opacity) > 0;
  }
  function focusables(el) {
    return [].slice
      .call(el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(isVisible);
  }
  function otherDialogOpen(pop) {
    var candidates = doc.querySelectorAll('cart-drawer.active, cart-drawer.animate, quick-add-modal[open], details[open] > [role="dialog"], [role="dialog"]:not([hidden])');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el === pop || pop.contains(el)) continue;
      if (el.tagName === 'CART-DRAWER' || el.tagName === 'QUICK-ADD-MODAL') return true;
      if (isVisible(el)) return true;
    }
    return false;
  }

  function initPopup(scope) {
    scope.querySelectorAll('[data-hp-popup]').forEach(function (pop) {
      if (pop.dataset.hpBound) return;
      pop.dataset.hpBound = '1';
      var key = pop.dataset.hpPopupKey || 'hp-popup';
      var delay = parseFloat(pop.dataset.hpPopupDelay || '5') * 1000;
      var days = parseFloat(pop.dataset.hpPopupDays || '7');
      var design = !!(window.Shopify && window.Shopify.designMode);
      var lastFocus = null;
      var timer = null;
      var retries = 0;

      function seen() {
        try {
          var t = parseInt(window.localStorage.getItem(key) || '0', 10);
          return t && Date.now() - t < days * 864e5;
        } catch (e) {
          return false;
        }
      }
      function remember() {
        try {
          window.localStorage.setItem(key, String(Date.now()));
        } catch (e) { /* ignore */ }
      }
      function open(force) {
        if (!pop.hidden) return;
        if (!force && !design && otherDialogOpen(pop)) {
          // Another dialog has the focus; try again shortly (up to ~90 s).
          if (retries++ < 60) timer = setTimeout(function () { open(); }, 1500);
          return;
        }
        lastFocus = doc.activeElement;
        pop.hidden = false;
        doc.body.classList.add('hp-popup-open');
        if (!design) remember(); // counted as shown the moment it opens
        requestAnimationFrame(function () {
          pop.classList.add('is-open');
          var f = focusables(pop);
          var first = f.filter(function (el) { return !el.hasAttribute('data-hp-popup-close'); })[0] || f[0];
          if (first) first.focus();
        });
      }
      function close() {
        if (pop.hidden) return;
        pop.classList.remove('is-open');
        doc.body.classList.remove('hp-popup-open');
        setTimeout(function () {
          pop.hidden = true;
        }, reduce ? 0 : 260);
        if (lastFocus && lastFocus.focus && doc.contains(lastFocus)) lastFocus.focus();
      }

      pop.querySelectorAll('[data-hp-popup-close], .hp-popup__bg').forEach(function (b) {
        b.addEventListener('click', function () {
          close();
        });
      });
      function onKey(e) {
        if (pop.hidden) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
          return;
        }
        if (e.key !== 'Tab') return;
        var f = focusables(pop);
        if (!f.length) {
          e.preventDefault();
          return;
        }
        var first = f[0];
        var last = f[f.length - 1];
        var active = doc.activeElement;
        if (e.shiftKey && (active === first || !pop.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !pop.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
      doc.addEventListener('keydown', onKey);

      function onSelect(e) {
        if (e.target === pop.closest('.shopify-section')) open(true);
      }
      function onDeselect(e) {
        if (e.target === pop.closest('.shopify-section')) close();
      }

      if (pop.hasAttribute('data-hp-popup-open')) {
        open(true);
      } else if (design) {
        // In the editor, only open when the merchant selects the section.
        doc.addEventListener('shopify:section:select', onSelect);
        doc.addEventListener('shopify:section:deselect', onDeselect);
      } else if (!seen()) {
        timer = setTimeout(function () { open(); }, delay);
      }
      pop.hpOpen = open;
      pop.hpClose = close;
      onUnload(pop, function () {
        clearTimeout(timer);
        doc.removeEventListener('keydown', onKey);
        doc.removeEventListener('shopify:section:select', onSelect);
        doc.removeEventListener('shopify:section:deselect', onDeselect);
        doc.body.classList.remove('hp-popup-open');
      });
    });
  }

  /* ---------------- ambient (muted autoplay) video ---------------- */
  function initAmbient(scope) {
    scope.querySelectorAll('[data-hp-ambient] video').forEach(function (v) {
      if (v.dataset.hpBound) return;
      v.dataset.hpBound = '1';
      v.muted = true;
      v.setAttribute('playsinline', '');
      v.removeAttribute('autoplay');
      if (reduce) {
        v.pause();
        return; // the visible toggle button still lets the visitor start it
      }
      if (!supportsIO) {
        playVideo(v);
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              if (!v.dataset.hpUserPaused) playVideo(v);
            } else {
              v.pause();
            }
          });
        },
        { threshold: 0.15 }
      );
      io.observe(v);
      onUnload(v, function () {
        io.disconnect();
      });
    });
  }

  /* ---------------- click-to-copy ---------------- */
  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () {
        return legacyCopy(text);
      });
    }
    return legacyCopy(text);
  }
  function legacyCopy(text) {
    var ok = false;
    var ta = doc.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    doc.body.appendChild(ta);
    ta.select();
    try {
      ok = doc.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    doc.body.removeChild(ta);
    return ok ? Promise.resolve() : Promise.reject(new Error('copy failed'));
  }
  function initCopy(scope) {
    scope.querySelectorAll('[data-hp-copy]').forEach(function (btn) {
      if (btn.dataset.hpBound) return;
      btn.dataset.hpBound = '1';
      var label = btn.querySelector('[data-hp-copy-label]') || btn;
      var original = label.textContent;
      var live = btn.querySelector('[data-hp-copy-live]');
      if (!live) {
        live = doc.createElement('span');
        live.className = 'visually-hidden';
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('data-hp-copy-live', '');
        btn.appendChild(live);
      }
      btn.addEventListener('click', function () {
        copyText(btn.dataset.hpCopy || '').then(function () {
          btn.classList.add('is-copied');
          var msg = btn.dataset.hpCopied || '✓';
          label.textContent = msg;
          live.textContent = msg;
          setTimeout(function () {
            btn.classList.remove('is-copied');
            label.textContent = original;
          }, 1800);
        }).catch(function () {
          // Nothing was copied (blocked clipboard): keep the code visible and selectable instead.
          try {
            var sel = window.getSelection();
            var range = doc.createRange();
            range.selectNodeContents(label);
            sel.removeAllRanges();
            sel.addRange(range);
          } catch (e) { /* ignore */ }
        });
      });
    });
  }

  /* ---------------- boot ---------------- */
  function safe(fn, scope) {
    try {
      fn(scope);
    } catch (e) {
      if (window.console && console.error) console.error('[harper-motion]', e);
    }
  }

  var ready = false;
  function init(scope) {
    scope = scope || doc;
    if (!ready) {
      ready = true;
      // From this point the CSS may hide [data-hp-reveal] elements; the very next
      // call marks everything already on screen as visible, in the same task.
      root.classList.add('hp-motion-ready');
    }
    safe(initReveal, scope);
    safe(initStats, scope);
    safe(initVideoToggles, scope);
    safe(initMarquee, scope);
    safe(initTabs, scope);
    safe(initVideos, scope);
    safe(initStickyAtc, scope);
    safe(initCompare, scope);
    safe(initPopup, scope);
    safe(initAmbient, scope);
    safe(initCopy, scope);
    safe(initToTop, scope);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', function () {
      init(doc);
    });
  } else {
    init(doc);
  }

  doc.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  doc.addEventListener('shopify:section:unload', function (e) {
    if (!cleanups) return;
    var list = cleanups.get(e.target);
    if (!list) return;
    list.forEach(function (fn) {
      try {
        fn();
      } catch (err) { /* ignore */ }
    });
    cleanups.delete(e.target);
  });
  // Theme editor: when a block is picked in the sidebar, make sure it is actually visible.
  doc.addEventListener('shopify:block:select', function (e) {
    var el = e.target;
    if (!el || !el.hasAttribute) return;
    if (el.hasAttribute('data-hp-reveal')) enter(el);
    var revealParent = el.closest('[data-hp-reveal]');
    if (revealParent) enter(revealParent);
    // A tab block → switch to its video.
    if (el.getAttribute('role') === 'tab') {
      var group = el.closest('[data-hp-tabs]');
      if (group && group.hpActivate) group.hpActivate(el);
    }
    // An FAQ question → open it.
    if (el.tagName === 'DETAILS') el.open = true;
  });

  window.HarperMotion = { init: init, copyText: copyText };
})();
